<template>
 <div class="cuentas-wrapper">
   <ModalLoading :isLoading="isLoading" />

   <div class="opciones">
     <!-- Filtros de búsqueda -->
     <div class="search-bar">
       <input
         class="busqueda"
         type="text"
         v-model="searchQuery"
         placeholder="Buscar cliente..."
       />
     </div>

     <!-- Filtro de estado -->
     <div class="status-filter">
       <label for="status-select">Estado: </label>
       <select
         id="status-select"
         v-model="selectedStatus"
         class="status-select"
       >
         <option value="">Todos</option>
         <option value="Pendiente">Pendientes</option>
         <option value="Pagado">Pagados</option>
         <option value="Parcial">Pagos Parciales</option>
       </select>
     </div>

     <!-- Filtro sucursal CEO -->
     <div v-if="esCeo" class="status-filter">
       <label>Sucursal:</label>
       <select class="status-select" v-model="filtroSucursal" @change="onCambioSucursal">
         <option value="">Todas las sucursales</option>
         <option v-for="s in sucursalesCeo" :key="s.id_sucursal" :value="s.id_sucursal">
           {{ s.nombre_administrativo }}
         </option>
       </select>
     </div>

     <!-- Botón de exportación PDF -->
     <ExportButton
       :columns="columns"
       :rows="allFilteredRows"
       fileName="CuentasPorCobrar.pdf"
       class="export-button"
     />
   </div>

   <!-- Tabla de cuentas por cobrar -->
   <div class="table-container" ref="table">
     <!-- Indicador de carga -->
     <div v-if="loading" class="loading-indicator">Cargando cuentas por cobrar...</div>

     <!-- Mensaje si no hay datos -->
     <div v-else-if="!paginatedCuentas || paginatedCuentas.length === 0" class="no-data">
       No se encontraron cuentas por cobrar.
     </div>

     <table v-else class="table">
       <thead>
         <tr>
           <th>#</th>
           <th>Cliente</th>
           <th>RTN</th>
           <th>Total Facturas</th>
           <th>Monto Original</th>
           <th>Monto Pagado</th>
           <th>Saldo Pendiente</th>
           <th>Estado</th>
           <th>Última Actualización</th>
           <th>Acciones</th>
         </tr>
       </thead>
       <tbody>
         <tr v-for="(cuenta, index) in paginatedCuentas || []" :key="cuenta.id_cliente || index">
           <td data-label="#">
             {{ String((currentPage - 1) * pageSize + index + 1).padStart(3, " ") }}
           </td>
           <td data-label="Cliente">{{ cuenta.nombre_cliente }}</td>
           <td data-label="RTN">{{ cuenta.rtn_cliente }}</td>
           <td data-label="Total Facturas">{{ cuenta.total_facturas }}</td>
           <td data-label="Monto Original">{{ formatCurrency(cuenta.monto_original_total) }}</td>
           <td data-label="Monto Pagado">{{ formatCurrency(cuenta.monto_pagado_total) }}</td>
           <td data-label="Saldo Pendiente">{{ formatCurrency(cuenta.saldo_pendiente_total) }}</td>
           <td data-label="Estado">
             <span
               :class="{
                 'estado-badge': true,
                 'estado-pendiente': cuenta.estado === 'Pendiente',
                 'estado-pagado': cuenta.estado === 'Pagado',
                 'estado-parcial': cuenta.estado === 'Parcial',
               }"
             >
               {{ cuenta.estado }}
             </span>
           </td>
           <td data-label="Última Actualización">
             {{ formatDateTime(cuenta.updated_at) }}
           </td>
           <td class="actions-column" data-label="Acciones">
             <button
               v-if="puedeCobrarCxC"
               class="accion-btn accion-cobrar"
               @click="abrirModalPago(cuenta)"
               :title="cuenta.estado === 'Pagado' ? 'Cuenta ya pagada' : 'Gestionar Pagos'"
               :disabled="cuenta.estado === 'Pagado'"
             >
               <i class="bi bi-cash-coin"></i>
             </button>
             <button
               class="accion-btn accion-historial"
               @click="verHistorialPagos(cuenta)"
               title="Ver Historial"
             >
               <i class="bi bi-clock-history"></i>
             </button>
           </td>
         </tr>
       </tbody>
     </table>

     <!-- Paginación -->
     <div class="pagination-wrapper">
       <div class="pagination-info">
         Mostrando {{ (currentPage - 1) * pageSize + 1 }} a
         {{ Math.min(currentPage * pageSize, filteredCuentas.length) }} de
         {{ filteredCuentas.length }} registros
       </div>
       <div class="pagination-container">
         <button
           class="pagination-button"
           :disabled="currentPage === 1"
           @click="previousPage"
         >
           Anterior
         </button>
         <button
           class="pagination-button"
           :disabled="currentPage === totalPages"
           @click="nextPage"
         >
           Siguiente
         </button>
       </div>
     </div>
   </div>

   <!-- Modal de Pago -->
   <PagarCuentaModal
     v-if="isPagoModalOpen"
     :isVisible="isPagoModalOpen"
     :cliente="clienteSeleccionado"
     :id_usuario="id_usuario"
     @close="cerrarModalPago"
     @pago-realizado="handlePagoRealizado"
   />

   <!-- Modal de Historial -->
   <HistorialPagosModal
     v-if="isHistorialModalOpen"
     :isVisible="isHistorialModalOpen"
     :cliente="clienteSeleccionado"
     :id_usuario="id_usuario"
     @close="cerrarModalHistorial"
   />
 </div>
</template>

<script>
import { useToast } from "vue-toastification";
import ExportButton from "../components/ExportButton.vue";
import PagarCuentaModal from "../components/PagarCuentaModal.vue";
import HistorialPagosModal from "../components/HistorialPagosModal.vue";
import solicitudes from "../../services/solicitudes.js";
import { hasAccionPermiso } from '../../services/permisos.js';
import CuentasPorCobrarService, { obtenerCuentasPorCobrarPorSucursal } from "../../services/cuentasPorCobrarSolicitudes.js";
import { getSucursalesbyEmmpresaSumm } from "../../services/sucursalesSolicitudes.js";
import { setPageTitle } from "@/components/pageMetadata";
const { getSocket } = require('@/services/socket');
import ModalLoading from "@/components/ModalLoading.vue";

export default {
 name: "CuentasPorCobrar",
 components: {
   ExportButton,
   PagarCuentaModal,
   HistorialPagosModal,
   ModalLoading,
 },
 setup() {
   const toast = useToast();
   return { toast };
 },
 data() {
   return {
     titulo: "Cuentas por Cobrar",
     searchQuery: "",
     selectedStatus: "",
     id_usuario: "",
     esCeo: false,
     sucursalesCeo: [],
     filtroSucursal: "",
     clienteSeleccionado: null,
     isPagoModalOpen: false,
     isHistorialModalOpen: false,
     isLoading: false,
     cuentas: [], // ⚠️ ASEGURAR QUE SIEMPRE SEA ARRAY
     currentPage: 1,
     pageSize: 10,
     columns: [
       { header: "#", dataKey: "index" },
       { header: "Cliente", dataKey: "nombre_cliente" },
       { header: "RTN", dataKey: "rtn_cliente" },
       { header: "Total Facturas", dataKey: "total_facturas" },
       { header: "Monto Original", dataKey: "monto_original_total" },
       { header: "Monto Pagado", dataKey: "monto_pagado_total" },
       { header: "Saldo Pendiente", dataKey: "saldo_pendiente_total" },
       { header: "Estado", dataKey: "estado" },
       { header: "Última Actualización", dataKey: "updated_at" },
     ],
     loading: false,
     error: null,
   };
 },
 computed: {
   puedeCobrarCxC() { return hasAccionPermiso('cxc_cobrar'); },
   filteredCuentas() {
     // ⚠️ VALIDACIÓN: Asegurar que cuentas sea un array
     if (!Array.isArray(this.cuentas)) {
       return [];
     }
     
     return this.cuentas.filter((cuenta) => {
       // ⚠️ VALIDACIÓN: Asegurar que cuenta existe y tiene nombre (RTN puede estar vacío)
       if (!cuenta || !cuenta.nombre_cliente) {
         return false;
       }

       const matchesSearchQuery =
         cuenta.nombre_cliente.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
         (cuenta.rtn_cliente || '').includes(this.searchQuery);

       const matchesStatus =
         !this.selectedStatus || cuenta.estado === this.selectedStatus;

       return matchesSearchQuery && matchesStatus;
     });
   },
   
   paginatedCuentas() {
     // ⚠️ VALIDACIÓN: Asegurar que filteredCuentas existe
     const filtered = this.filteredCuentas || [];
     
     const startIndex = (this.currentPage - 1) * this.pageSize;
     const endIndex = startIndex + this.pageSize;
     
     const paginated = filtered.slice(startIndex, endIndex);
     
     
     return paginated;
   },
   
   totalPages() {
     const filtered = this.filteredCuentas || [];
     return Math.ceil(filtered.length / this.pageSize);
   },
   
   allFilteredRows() {
     const filtered = this.filteredCuentas || [];
     return filtered.map((cuenta, index) => ({
       index: index + 1,
       nombre_cliente: cuenta.nombre_cliente || '',
       rtn_cliente: cuenta.rtn_cliente || '',
       total_facturas: cuenta.total_facturas || 0,
       monto_original_total: this.formatCurrency(cuenta.monto_original_total || 0),
       monto_pagado_total: this.formatCurrency(cuenta.monto_pagado_total || 0),
       saldo_pendiente_total: this.formatCurrency(cuenta.saldo_pendiente_total || 0),
       estado: cuenta.estado || '',
       updated_at: this.formatDateTime(cuenta.updated_at || new Date()),
     }));
   },
 },
 methods: {
   formatCurrency(value) {
     return `L. ${Number(value).toFixed(2)}`;
   },

   formatDateTime(dateString) {
     if (!dateString) return '—';
     const date = new Date(dateString);
     if (isNaN(date.getTime())) return '—';
     return date
       .toLocaleString("es-HN", {
         year: "numeric",
         month: "2-digit",
         day: "2-digit",
         hour: "2-digit",
         minute: "2-digit",
         hour12: true,
       })
       .replace(",", "");
   },

   async cargarCuentasPorCobrar() {
     
     this.loading = true;
     this.error = null;
     
     try {
       
       if (!this.id_usuario) {
         throw new Error('No se ha identificado el usuario');
       }
       
       const response = await CuentasPorCobrarService.obtenerCuentasPorCobrar(this.id_usuario, this.filtroSucursal || null);

       if (response && Array.isArray(response) && response.length > 0) {
         this.cuentas = response.map((cuenta) => ({
           ...cuenta,
           estado: this.determinarEstado(cuenta),
         }));
       } else {
         this.cuentas = []; // ⚠️ ASEGURAR QUE SEA ARRAY VACÍO
       }
       
       // ⚠️ VALIDACIÓN FINAL
       if (!Array.isArray(this.cuentas)) {
         console.error('❌ ERROR: cuentas no es un array después de la asignación');
         this.cuentas = [];
       }
       
     } catch (error) {
       console.error("💥 Error al cargar cuentas por cobrar:", error);
       this.error = "No se pudieron cargar las cuentas por cobrar.";
       this.toast.error(this.error);
       this.cuentas = []; // ⚠️ ASEGURAR QUE SEA ARRAY VACÍO EN ERROR
     } finally {
       this.loading = false;
     }
   },

   determinarEstado(cuenta) {
     if (cuenta.saldo_pendiente_total <= 0) {
       return "Pagado";
     } else if (cuenta.monto_pagado_total > 0) {
       return "Parcial";
     } else {
       return "Pendiente";
     }
   },

   abrirModalPago(cuenta) {
     
     if (cuenta.estado === "Pagado") {
       this.toast.info("Esta cuenta ya está completamente pagada");
       return;
     }
     
     this.clienteSeleccionado = cuenta;
     
     this.isPagoModalOpen = true;
     
   },

   cerrarModalPago() {
     this.isPagoModalOpen = false;
     this.clienteSeleccionado = null;
   },

   verHistorialPagos(cuenta) {
     this.clienteSeleccionado = cuenta;
     this.isHistorialModalOpen = true;
   },

   cerrarModalHistorial() {
     this.isHistorialModalOpen = false;
     this.clienteSeleccionado = null;
   },

   async handlePagoRealizado() {
     this.toast.success("Pago registrado exitosamente");
     await this.cargarCuentasPorCobrar();
     this.cerrarModalPago();
   },

   async onCambioSucursal() {
     this.currentPage = 1;
     if (this.filtroSucursal) {
       await this.cargarCuentasPorCobrarSucursal(this.filtroSucursal);
     } else {
       await this.cargarCuentasPorCobrar();
     }
   },

   async cargarCuentasPorCobrarSucursal(id_sucursal) {
     this.loading = true;
     this.error = null;
     try {
       const response = await obtenerCuentasPorCobrarPorSucursal(id_sucursal);
       this.cuentas = Array.isArray(response) ? response : [];
     } catch (error) {
       console.error('Error al cargar cuentas por sucursal:', error);
       this.cuentas = [];
     } finally {
       this.loading = false;
     }
   },

   previousPage() {
     if (this.currentPage > 1) {
       this.currentPage--;
     }
   },

   nextPage() {
     if (this.currentPage < this.totalPages) {
       this.currentPage++;
     }
   },

   resetFilters() {
     this.searchQuery = "";
     this.selectedStatus = "";
     this.currentPage = 1;
   },
 },
 watch: {
   searchQuery() {
     this.currentPage = 1;
   },
   selectedStatus() {
     this.currentPage = 1;
   },
   
   isPagoModalOpen(newVal) {
     if (newVal) {
       this.$nextTick(() => {});
     }
   }
 },

 async mounted() {
   setPageTitle("Cuentas por Cobrar");
   this.isLoading = true;
   
   
   
   try {
     this.id_usuario = await solicitudes.solicitarUsuarioToken();
     const rol = Number(localStorage.getItem('role') || 0);
     this.esCeo = rol === 4;
     if (this.esCeo) {
       this.sucursalesCeo = await getSucursalesbyEmmpresaSumm(this.id_usuario);
       if (!Array.isArray(this.sucursalesCeo)) this.sucursalesCeo = [];
     }
     await this.cargarCuentasPorCobrar();
   } catch (error) {
     console.error("💥 Error en mounted:", error);
     this.toast.error("Error al inicializar la página: " + error.message);
     
     // ⚠️ ASEGURAR ESTADO SEGURO EN CASO DE ERROR
     this.cuentas = [];
     this.id_usuario = null;
     
   } finally {
     this.isLoading = false;
   }
   const _s = getSocket();
   if (_s) { this._wsRefresh = () => this.cargarCuentasPorCobrar(); _s.on('cuentas-cobrar:refresh', this._wsRefresh); }
 },
 beforeUnmount() {
   const _s = getSocket();
   if (_s && this._wsRefresh) _s.off('cuentas-cobrar:refresh', this._wsRefresh);
 },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap");

* {
 font-family: "Montserrat", sans-serif;
 box-sizing: border-box;
}

.busqueda {
 padding: 10px;
 font-size: 14px;
 border-radius: 10px;
 border-width: 0.5px;
 width: 100%;
 max-width: 300px;
}

input:focus,
select:focus {
 outline: none;
 border-color: #c09d62;
 box-shadow: 0 0 0 3px rgba(192, 157, 98, 0.2);
 transition: all 0.3s ease;
}

.status-filter {
 display: flex;
 align-items: center;
 gap: 10px;
}

.status-select {
 padding: 8px;
 border-radius: 10px;
 border: 1px solid #ced4da;
 font-size: 14px;
 min-width: 120px;
 cursor: pointer;
}

.export-button {
 margin: 0;
 z-index: 1000;
}

.accion-btn {
 display: inline-flex;
 align-items: center;
 justify-content: center;
 width: 32px;
 height: 32px;
 font-size: 16px;
 border-radius: 6px;
 color: white;
 border: none;
 cursor: pointer;
 margin: 0 2px;
 transition: opacity 0.15s, transform 0.15s;
}

.accion-btn:hover {
 opacity: .85;
 transform: scale(1.08);
}

.accion-cobrar {
 background-color: #198754;
}

.accion-historial {
 background-color: #3b82f6;
}

.accion-btn:disabled {
 opacity: 0.4;
 cursor: not-allowed;
 transform: none;
}

.estado-badge {
 padding: 3px 6px;
 font-size: 0.75rem;
 border-radius: 12px;
 font-weight: 500;
}

.estado-pendiente {
 background-color: #fff3cd;
 color: #856404;
}

.estado-pagado {
 background-color: #d1e7dd;
 color: #0f5132;
}

.estado-parcial {
 background-color: #d1ecf1;
 color: #0c5460;
}

.actions-column {
 display: flex;
 gap: 8px;
 justify-content: center;
}

.cuentas-wrapper {
 padding: 16px;
 width: 100%;
 overflow-x: hidden;
}

.opciones {
 display: flex;
 align-items: center;
 flex-wrap: wrap;
 gap: 16px;
 margin-bottom: 16px;
}

.table-container {
 width: 100%;
 overflow-x: auto;
 -webkit-overflow-scrolling: touch;
 margin-bottom: 20px;
 border-radius: 8px;
 border: 1px solid #e2e8f0;
}

.table {
 width: 100%;
 min-width: auto;
 margin-bottom: 0;
 white-space: nowrap;
}

.table th,
.table td {
 border: 1px solid #dee2e6;
 padding: 6px 8px;
 font-size: 0.875rem;
 white-space: nowrap;
 text-align: center;
 overflow: hidden;
 text-overflow: ellipsis;
}

.table th {
 font-weight: bold;
 position: sticky;
 top: 0;
 background-color: white;
 z-index: 1;
}

.table-container::-webkit-scrollbar {
 width: 8px;
 height: 8px;
}

.table-container::-webkit-scrollbar-track {
 background: #f1f1f1;
 border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb {
 background: #c09d62;
 border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb:hover {
 background: #a38655;
}

.pagination-wrapper {
 display: flex;
 justify-content: space-between;
 align-items: center;
 padding: 1rem;
 border-top: 1px solid #dee2e6;
 margin-top: auto;
}

.pagination-info {
 color: #6c757d;
}

.pagination-container {
 display: flex;
 gap: 5px;
}

.pagination-button {
 padding: 8px 16px;
 border: 1px solid #dee2e6;
 background-color: white;
 cursor: pointer;
 border-radius: 4px;
 min-width: 40px;
 transition: all 0.3s ease;
}

.pagination-button:hover:not(:disabled) {
 background-color: #e9ecef;
}

.pagination-button:disabled {
 cursor: not-allowed;
 opacity: 0.6;
}

.loading-indicator,
.no-data {
 text-align: center;
 padding: 2rem;
 font-size: 1.1rem;
 color: #666;
}

.no-data {
 background-color: #f8f9fa;
 border-radius: 4px;
}

/* Modo oscuro */
.dark .table-container {
 border-color: #404040;
 background-color: #2d2d2d;
}

.dark .table th {
 background-color: #383838;
 color: #fff;
 border-color: #404040;
}

.dark .table td {
 color: #fff;
 border-color: #404040;
}

.dark .loading-indicator,
.dark .no-data {
 color: #aaa;
 background-color: #2d2d2d;
}

/* Media Queries */
@media screen and (max-width: 768px) {
 .cuentas-wrapper {
   padding: 12px;
 }

 .opciones {
   display: grid;
   grid-template-columns: 1fr;
   gap: 12px;
   margin-bottom: 16px;
 }

 .status-filter,
 .search-bar {
   width: 100%;
 }

 .status-select,
 .busqueda {
   width: 100%;
   max-width: none;
 }

 .export-button {
   width: 100%;
   padding: 10px;
   justify-content: center;
 }

 .table-container {
   margin: 10px -12px;
   border-radius: 0;
   border-left: none;
   border-right: none;
 }

 .table {
   min-width: 700px;
 }

 .table th,
 .table td {
   padding: 8px 6px;
   font-size: 14px;
 }

 .actions-column {
   gap: 4px;
 }

 .estado-badge {
   padding: 4px 6px;
   font-size: 12px;
 }
}

@media screen and (max-width: 480px) {
 .table thead {
   display: none;
 }

 .table tbody {
   display: block;
 }

 .table tr {
   display: block;
   margin-bottom: 12px;
   padding: 12px;
   border: 1px solid #ddd;
   border-radius: 8px;
   background: #fff;
 }

 .table td {
   display: grid;
   grid-template-columns: 40% 60%;
   padding: 8px 4px;
   text-align: left;
   border: none;
   font-size: 14px;
   white-space: normal;
 }

 .table td::before {
   content: attr(data-label);
   font-weight: bold;
   color: #666;
 }

 .actions-column {
   display: flex;
   justify-content: flex-end;
   gap: 8px;
   padding-top: 12px;
   margin-top: 8px;
   border-top: 1px solid #eee;
   grid-column: 1 / -1;
 }

 .dark .table tr {
   background: #2d2d2d;
   border-color: #404040;
 }

 .dark .table td::before {
   color: #aaa;
 }

 .dark .actions-column {
   border-top-color: #404040;
 }
}
</style>
